import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveErrorMessageKey } from '@constants/error-codes/error-code-map';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import {
  InfiniteData,
  QueryFunction,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { computeQueryEnabled } from '@utils/auth/compute-query-enabled';
import { RequestError } from '@utils/request/request.type';
import { showToastErrorRequest } from '@utils/show-toast-error-request/show-toast-error-request';

import { useRefreshTokenHandler } from './auth/use-refresh-token-handler';
import { useTokenManagement } from './auth/use-token-management';
import { useToast } from './use-toast/use-toast';

export type GenericInfiniteQueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
> = Omit<
  UseInfiniteQueryOptions<TQueryFnData, TError, InfiniteData<TData, TPageParam>, TQueryKey, TPageParam>,
  'queryFn' | 'queryKey' | 'getNextPageParam' | 'initialPageParam' | 'select'
> & {
  getNextPageParam?: UseInfiniteQueryOptions<
    TQueryFnData,
    TError,
    InfiniteData<TData, TPageParam>,
    TQueryKey,
    TPageParam
  >['getNextPageParam'];
  initialPageParam?: TPageParam;
  onSuccess?: (data: InfiniteData<TData, TPageParam>) => void;
  onError?: (error: TError) => void;
  onSettled?: (data: InfiniteData<TData, TPageParam> | undefined, error: TError | null) => void;
  keepPreviousData?: boolean;
  hideError?: boolean;
};

export const useGenericInfiniteQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
>(
  key: TQueryKey,
  fn: QueryFunction<TQueryFnData, TQueryKey, TPageParam>,
  options?: GenericInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
  hideError?: boolean
): UseInfiniteQueryResult<InfiniteData<TData, TPageParam>, TError> => {
  const { isPublicEventReportPage } = useContext(FeatureFlagContext);
  const toast = useToast();
  const { t } = useTranslation();

  const { getTokens } = useTokenManagement();
  const { accessToken, refreshToken } = getTokens();

  const { createRefreshAndRetryHandler } = useRefreshTokenHandler();
  const handleRefreshAndRetry = createRefreshAndRetryHandler<
    Parameters<QueryFunction<TQueryFnData, TQueryKey, TPageParam>>[0],
    TQueryFnData | Promise<TQueryFnData>
  >((context) => fn(context));

  // QUERY FUNCTION WITH IMMEDIATE RETRY ON 401
  const queryFnWithRetry: QueryFunction<TQueryFnData, TQueryKey, TPageParam> = async (context) => {
    try {
      return await fn(context);
    } catch (error: unknown) {
      if (error instanceof RequestError && error.status === 401) {
        return await handleRefreshAndRetry(context);
      }
      // If not 401, propagate the error
      throw error;
    }
  };

  // Destructure custom options out to avoid passing them to useInfiniteQuery
  const {
    onSuccess,
    onError,
    onSettled,
    keepPreviousData,
    hideError: optionsHideError,
    enabled: optionsEnabled,
    initialPageParam,
    getNextPageParam,
    ...queryOptions
  } = options ?? {};

  // Determine if query should be enabled
  const finalEnabled = computeQueryEnabled({
    isPublicEventReportPage,
    accessToken,
    refreshToken,
    optionsEnabled: optionsEnabled as boolean | undefined,
  });

  const query = useInfiniteQuery<TQueryFnData, TError, InfiniteData<TData, TPageParam>, TQueryKey, TPageParam>({
    queryKey: key,
    queryFn: queryFnWithRetry,
    ...queryOptions,
    initialPageParam: (initialPageParam ?? 0) as TPageParam,
    getNextPageParam: getNextPageParam ?? (() => undefined),
    enabled: finalEnabled,
    placeholderData: keepPreviousData ? (previousData) => previousData : undefined,
    retry: (failureCount: number, error: unknown) => {
      // Don't retry on 401 - already handled by queryFnWithRetry
      if (error instanceof RequestError && error.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Handle error state - show toast and call onError callback
  useEffect(() => {
    if (query.isError) {
      const error = query.error;
      if (onError) onError(error);

      if (error instanceof RequestError) {
        const hError = optionsHideError ?? hideError;
        if (!hError && showToastErrorRequest(error)) {
          const messageKey = resolveErrorMessageKey(error.code);
          toast.push({
            title: messageKey ? t(messageKey) : error.message,
            text: '',
            type: 'error',
            disableTimeOut: true,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  // Handle success state - call onSuccess callback
  useEffect(() => {
    if (query.isSuccess) {
      if (onSuccess) onSuccess(query.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.data]);

  // Handle settled state - call onSettled callback when query is no longer fetching
  useEffect(() => {
    const isSettled = !query.isFetching && (query.isSuccess || query.isError);
    if (isSettled) {
      if (onSettled) onSettled(query.data, query.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isFetching, query.isSuccess, query.isError, query.data, query.error]);

  return query;
};
